import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Minimal portal modal for the M6a coach list-page surfaces
 * (Roster Quick Add / Edit / Link, the playlist composer, the
 * match-summary composer, the goal modal).
 *
 * Deliberately small — a full design-system `<Modal>` (Radix /
 * focus-trap / animation) is a later polish-pass concern, not M6a
 * scope. This mirrors the hand-rolled portal pattern already used
 * by `FeedbackPlayerModal` / `FocusModeOverlay`: a fixed backdrop,
 * a centered card, Escape + backdrop-click to close, a labelled
 * `role="dialog"`.
 *
 * The legacy `app.formModal()` enforced "one modal at a time" — in
 * React that's the caller's job (don't render two `<CoachModal>`s
 * at once); the M6a pages render at most one open modal in their
 * state, so the constraint holds structurally.
 */

export type CoachModalProps = {
  /** Heading text — also the dialog's accessible name. */
  title: string;
  /** Close handler — backdrop click, Escape, the X button. */
  onClose: () => void;
  children: ReactNode;
  /** Optional testid on the dialog element. */
  "data-testid"?: string;
};

export function CoachModal({
  title,
  onClose,
  children,
  ...rest
}: CoachModalProps) {
  // Escape closes — unless a ConfirmDialog is also open (it grabs
  // Escape first via its own useOneShotModal listener). Guard by
  // checking for the sentinel attribute so stacked portals don't
  // both close on the same keypress.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector("[data-confirm-dialog]")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={rest["data-testid"]}
      data-coach-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-lg border border-rule bg-surface p-5">
        <header className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-display tracking-tight text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="coach-modal-close"
            aria-label="Close"
            className="shrink-0 rounded border border-rule bg-bg-card px-2.5 py-1 text-sm text-ink hover:bg-surface transition-colors"
          >
            ✕
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}
